/**
 * P8-01B · Locale routing rules.
 *
 * Pure and dependency-free so the Edge middleware and the Node layer share one
 * definition of what a localised path looks like.
 *
 * ## Why a sentinel
 *
 * Storefront routes live under `app/[locale]/`, so *every* storefront URL needs
 * a first segment or it won't match. But brands set `hideDefaultLocalePath`,
 * meaning the default language should keep clean URLs (`/cart`, not `/en/cart`)
 * — and middleware runs on the Edge, where it can't reach Prisma to learn which
 * language that is.
 *
 * So middleware rewrites unprefixed paths to a **sentinel** segment and lets the
 * Node layer resolve the real locale (it already does: `src/i18n/request.ts`
 * reads cookie/header/brand config). The browser URL never changes.
 *
 *   /cart      → rewrite → /_/cart      → resolves to the brand default
 *   /fr/cart   → passthrough            → resolves to fr
 *   /en/cart   → Node redirect → /cart  → canonical, when the default is hidden
 *
 * The consequence, and the one rule to remember: **`params.locale` is not
 * authoritative** — it is the sentinel on default-locale requests. Route code
 * must call `getRequestLocale()` instead.
 */

import { isSupportedLanguage, languageOf } from "./config";

/**
 * Internal-only first segment standing in for "no explicit locale in the URL".
 * Chosen to be something no real language code or route could collide with.
 */
export const LOCALE_SENTINEL = "_";

/**
 * Top-level paths that are NOT localised storefront routes and must never be
 * rewritten: API handlers, the admin app, auth pages, and the component
 * sandbox. Everything else under `app/` is storefront and lives in `[locale]/`.
 *
 * Adding a new top-level non-storefront route means adding it here, or it will
 * be rewritten into the storefront tree and 404.
 */
export const NON_LOCALISED_PREFIXES = [
  "/api",
  "/admin",
  // P8-09 · The generated social card. An asset endpoint like `/api`, not a
  // page: it renders an image from a title passed in the query string, so a
  // locale segment would key the cache by a language the output never uses.
  "/og",
  // P17-13 · The manifest's icons (`/icon/192?v=…`). Same shape as `/og`: an
  // image endpoint keyed by brand + size, never by language. Found on prod
  // the night it shipped — the route answered Next's HTML 404 because the
  // rewrite sent `/icon/192` to `/_/icon/192`, which no page matches.
  "/icon",
  // P17-09 · The product feed. It only escaped the rewrite because its
  // filename carries a dot and the matcher's static-asset heuristic skipped
  // it; that is an accident, not a contract, so it is listed explicitly.
  "/feeds",
  "/kitchen-sink",
  // P14-12 · `/sign-in`, `/register` and `/verify` used to sit here, which is
  // why there was no `/fr/sign-in` at all: a French shopper who had browsed,
  // carted and checked out in French hit an English wall at the door. The
  // pages now live under `[locale]`, so they localise like every other
  // storefront route — and the unprefixed path still serves the default
  // language, which is what keeps the admin sign-in working unchanged.
] as const;

/** True when the path belongs to a non-localised area (admin, api, auth…). */
export function isNonLocalisedPath(pathname: string): boolean {
  return NON_LOCALISED_PREFIXES.some(
    (prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`),
  );
}

/**
 * The explicit language segment at the start of a path, or `null` when there
 * isn't one. `"/fr/cart"` → `"fr"`; `"/cart"` → `null` (there is no language
 * called "cart").
 */
export function pathLanguage(pathname: string): string | null {
  const first = pathname.split("/")[1];
  const lang = languageOf(first);
  return isSupportedLanguage(lang) ? lang : null;
}

/** True when the first segment is the internal sentinel. */
export function isSentinel(segment: string | null | undefined): boolean {
  return segment === LOCALE_SENTINEL;
}

/**
 * Strip a leading language (or sentinel) segment, returning the path as the
 * shopper sees it. `"/fr/cart"` → `"/cart"`; `"/_/cart"` → `"/cart"`.
 */
export function stripLocalePrefix(pathname: string): string {
  const segments = pathname.split("/");
  const first = segments[1];
  if (first && (isSentinel(first) || isSupportedLanguage(languageOf(first)))) {
    const rest = `/${segments.slice(2).join("/")}`;
    return rest === "/" ? "/" : rest.replace(/\/$/, "");
  }
  return pathname;
}

/**
 * P8-08 · Route families whose final segment is a generated slug, and are
 * therefore safe to lowercase.
 *
 * This list is an allowlist rather than a denylist on purpose. Most storefront
 * routes carry a **case-sensitive identifier** — `/checkout/confirmation/[orderId]`
 * and `/checkout/bank-transfer/[paymentId]` take cuids, `/subscribe/[variantId]`
 * takes a cuid, `/account/orders/[number]` takes an order number like `VX1001`.
 * Lowercasing any of those breaks the page outright, and they are checkout and
 * post-purchase paths, so the failure lands on shoppers who have already paid.
 *
 * The P8-08 ticket says "lowercase normalisation (301)" with no qualification.
 * Taken literally that is a checkout outage, so it is scoped to the routes whose
 * slugs `slugify` generates and which are lowercase by construction.
 */
const LOWERCASE_PATH_PREFIXES = ["/products/", "/c/", "/blog/"] as const;

export interface NormalisedPath {
  pathname: string;
  /** 308 preserves the request method; 301 is the SEO-canonical permanent. */
  status: 301 | 308;
}

/**
 * The canonical form of a request path, or null when it is already canonical.
 *
 * Two normalisations, deliberately carrying different status codes:
 *
 * - **Trailing slash** is stripped with a **308**, which preserves the request
 *   method. A form POSTing to a path with a trailing slash must stay a POST;
 *   a 301 would downgrade it to GET and silently drop the body.
 * - **Case** is folded with a **301**, the permanent that search engines
 *   consolidate link equity through — and only on the slug routes above.
 *
 * Operates on the pathname alone. Query strings are never touched: they carry
 * case-sensitive values (`?variant=cmXYZ`) and are not part of the canonical
 * URL for this purpose.
 */
export function normaliseRequestPath(pathname: string): NormalisedPath | null {
  if (isNonLocalisedPath(pathname)) return null;

  // Root is already canonical and must never be emptied.
  let next = pathname.length > 1 ? pathname.replace(/\/+$/, "") : pathname;
  if (next === "") next = "/";
  const trailingChanged = next !== pathname;

  const withoutLocale = stripLocalePrefix(next);
  const lowerable = LOWERCASE_PATH_PREFIXES.some((prefix) =>
    withoutLocale.toLowerCase().startsWith(prefix),
  );
  // Fold the path AFTER the language segment only. The segment itself is left
  // for the `[locale]` layout, which knows the brand's default language and
  // sends a redundant or mis-cased prefix to its canonical URL in ONE hop
  // (`localeRedirect`). Lowercasing it here too made `/en-GB/c/x` a chain:
  // 301 → `/en-gb/c/x`, then 308 → `/c/x`.
  const prefix = withoutLocale !== next ? `/${next.split("/")[1]}` : "";
  const rest = next.slice(prefix.length);
  const lowered = lowerable ? prefix + rest.toLowerCase() : next;
  const caseChanged = lowered !== next;

  if (!trailingChanged && !caseChanged) return null;
  // A case change is the stronger signal, so it wins the status when both
  // apply — the method-preserving 308 only matters for the slash-only case.
  return { pathname: lowered, status: caseChanged ? 301 : 308 };
}

/**
 * Build the browser-facing href for a path in a given language.
 *
 * `hideDefault` reflects the brand's `hideDefaultLocalePath`: when the target
 * language is the brand default and the default path is hidden, the href has no
 * prefix at all — which is what keeps existing URLs unchanged.
 */
export function localeHref(
  pathname: string,
  options: {
    language: string;
    defaultLanguage: string;
    hideDefault: boolean;
  },
): string {
  // Total by design: anything that isn't a localised storefront path comes back
  // untouched, so `LocaleLink` is safe to use indiscriminately. Without this,
  // every call site would have to remember that `/sign-in` and `/admin` live
  // outside `[locale]` — and prefixing them would 404 the auth flow.
  if (
    isNonLocalisedPath(pathname) ||
    !pathname.startsWith("/") ||
    pathname.startsWith("//")
  ) {
    return pathname;
  }

  const bare = stripLocalePrefix(pathname);
  const lang = languageOf(options.language) ?? options.defaultLanguage;
  const isDefault = lang === languageOf(options.defaultLanguage);

  if (isDefault && options.hideDefault) return bare;
  return bare === "/" ? `/${lang}` : `/${lang}${bare}`;
}

/**
 * Where a request under a `[locale]` segment must go instead, if anywhere —
 * the `[locale]` layout's decision, pure so it can be tested.
 *
 * - a disabled or unknown language → the unprefixed path, temporary (307): the
 *   brand may enable it tomorrow;
 * - the brand default with `hideDefault` → the unprefixed path, permanent;
 * - an enabled language spelled non-canonically (`/FR/…`, `/fr-FR/…`) → its
 *   canonical prefix, permanent.
 *
 * Every case is one hop: the edge no longer lowercases the segment first.
 */
export function localeRedirect(input: {
  /** The browser path, with its language segment. */
  pathname: string;
  /** The raw `[locale]` segment. */
  segment: string;
  enabled: readonly string[];
  defaultLanguage: string | null;
  hideDefault: boolean;
}): { target: string; permanent: boolean } | null {
  const language = languageOf(input.segment);
  const bare = stripLocalePrefix(input.pathname);
  if (language == null || !input.enabled.includes(language)) {
    return { target: bare, permanent: false };
  }
  if (language === input.defaultLanguage && input.hideDefault) {
    return { target: bare, permanent: true };
  }
  if (input.segment !== language) {
    return {
      target: localeHref(bare, {
        language,
        defaultLanguage: input.defaultLanguage ?? language,
        hideDefault: input.hideDefault,
      }),
      permanent: true,
    };
  }
  return null;
}
